import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltip } from '@angular/material/tooltip';
import { forkJoin, of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { TestPlanService } from '../../../services/test-plan.service';
import { TestCaseService } from '../../../services/test-case.service';
import { ProjectRoleService } from '../../../services/project-role.service';
import { ReleaseReportService } from '../../../services/release-report.service';
import { copyText } from '../../../shared/clipboard';
import { saveBlob } from '../../../shared/file-download';
import { realRunQueryParams, writeErrorKey } from '../test-plans.utils';
import { TestPlan, TestPlanReportDetails } from '../../../models/test-plan.model';
import { TestCase, TestExecutionStatus } from '../../../models/test-case.model';
import { PlanStatusPillComponent } from '../plan-status-pill/plan-status-pill.component';
import { ResultBadgeKind, ResultStatusBadgeComponent } from '../result-status-badge/result-status-badge.component';
import { ProjektArchiviertHinweisComponent } from '../../../components/projekt-archiviert-hinweis/projekt-archiviert-hinweis.component';

type ReportBasis = 'status' | 'schritte';
type StepCounts = TestPlanReportDetails['steps'];

const MESSAGE_DURATION_MS = 4000;

interface ReportRow {
  planPublicId: string | null;
  planId: string;
  testCaseKey: string;
  testCaseId: string;
  summary: string;
  status: ResultBadgeKind;
  requirementId: string | null;
  requirementLabel: string | null;
}

function statusFromSteps(steps: StepCounts): ResultBadgeKind {
  if (steps.failed > 0) return 'failed';
  if (steps.total > 0 && steps.passed === steps.total) return 'passed';
  if (steps.inProgress > 0 || steps.passed > 0) return 'in-progress';
  return 'open';
}

@Component({
  selector: 'app-test-plan-report',
  imports: [
    ProjektArchiviertHinweisComponent,
    CommonModule,
    RouterLink,
    TranslatePipe,
    MatIcon,
    MatProgressSpinner,
    MatRadioGroup,
    MatRadioButton,
    MatTooltip,
    PlanStatusPillComponent,
    ResultStatusBadgeComponent,
  ],
  templateUrl: './test-plan-report.component.html',
  styleUrl: './test-plan-report.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TestPlanReportComponent {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private planService = inject(TestPlanService);
  private testCaseService = inject(TestCaseService);
  private destroyRef = inject(DestroyRef);
  private projectRoleService = inject(ProjectRoleService);
  private releaseReportService = inject(ReleaseReportService);
  private location = inject(Location);
  private snackBar = inject(MatSnackBar);
  private translate = inject(TranslateService);

  readonly projectId: string;
  readonly planId: string;
  readonly loading = signal(true);
  readonly loadError = signal(false);
  readonly plan = signal<TestPlan | null>(null);
  readonly rows = signal<ReportRow[]>([]);
  readonly search = signal('');
  readonly basis = signal<ReportBasis>('status');
  readonly details = signal<ReadonlyMap<string, TestPlanReportDetails> | null>(null);
  readonly detailsFailed = signal(false);
  readonly pdfBusy = signal(false);
  readonly isArchived = this.projectRoleService.isArchived;
  readonly isReadOnly = computed(
    () =>
      this.projectRoleService.isArchived() ||
      (this.projectRoleService.isProjectReader() && !this.projectRoleService.isTenantAdmin()),
  );

  readonly canStart = computed(() => {
    const plan = this.plan();
    return (
      !!plan &&
      !this.isReadOnly() &&
      plan.status !== 'ABGESCHLOSSEN' &&
      !!plan.executionMode &&
      plan.executionMode !== 'MIXED' &&
      plan.testCaseIds.length > 0
    );
  });
  readonly startLabelKey = computed(() => {
    const progress = this.plan()?.progress;
    const unfinished = (progress?.open ?? 0) + (progress?.inProgress ?? 0);
    return unfinished > 0 ? 'testPlans.report.startOpen' : 'testPlans.report.startRetest';
  });

  readonly countsSteps = computed(() => this.basis() === 'schritte' && this.details() !== null);

  readonly filteredRows = computed(() => {
    const term = this.search().toLowerCase().trim();
    const rows = term
      ? this.rows().filter((r) => (r.testCaseKey + ' ' + r.summary).toLowerCase().includes(term))
      : this.rows();
    return rows.map((row) => {
      const entry = this.details()?.get(row.testCaseId);
      return {
        ...row,
        status: this.countsSteps() && entry ? statusFromSteps(entry.steps) : row.status,
        defects: entry?.defectCount ?? null,
      };
    });
  });

  private readonly figures = computed<StepCounts>(() => {
    const details = this.details();
    if (this.countsSteps() && details) {
      const sum: StepCounts = { total: 0, passed: 0, failed: 0, inProgress: 0, open: 0 };
      for (const { steps } of details.values()) {
        sum.total += steps.total;
        sum.passed += steps.passed;
        sum.failed += steps.failed;
        sum.inProgress += steps.inProgress;
        sum.open += steps.open;
      }
      return sum;
    }
    const progress = this.plan()?.progress;
    return {
      total: progress?.total ?? 0,
      passed: progress?.passed ?? 0,
      failed: (progress?.failed ?? 0) + (progress?.blocked ?? 0),
      inProgress: progress?.inProgress ?? 0,
      open: progress?.open ?? 0,
    };
  });

  readonly totalCount = computed(() => this.figures().total);
  readonly passedCount = computed(() => this.figures().passed);
  readonly failedCount = computed(() => this.figures().failed);
  readonly inProgressCount = computed(() => this.figures().inProgress);
  readonly openCount = computed(() => this.figures().open);

  readonly completedPercent = computed(() => {
    const total = this.totalCount() || 1;
    return Math.round(((this.passedCount() + this.failedCount()) / total) * 100);
  });

  readonly segments = computed(() => {
    const total = this.totalCount() || 1;
    const rawSegments: { kind: ResultBadgeKind; value: number }[] = [
      { kind: 'passed', value: this.passedCount() },
      { kind: 'failed', value: this.failedCount() },
      { kind: 'in-progress', value: this.inProgressCount() },
      { kind: 'open', value: this.openCount() },
    ];
    return rawSegments.filter((s) => s.value > 0).map((s) => ({ ...s, percent: Math.round((s.value / total) * 100) }));
  });

  constructor() {
    this.projectId = this.route.snapshot.paramMap.get('projectId') ?? '';
    this.planId = this.route.snapshot.paramMap.get('planId') ?? '';
    const query = this.route.snapshot.queryParamMap;
    this.search.set(query.get('suche') ?? '');
    this.basis.set(query.get('basis') === 'schritte' ? 'schritte' : 'status');
    this.loadReport();
    this.loadDetails();
  }

  private loadReport(): void {
    if (!this.planId) {
      this.loadError.set(true);
      this.loading.set(false);
      return;
    }
    forkJoin({
      plan: this.planService.getById(this.projectId, this.planId).pipe(catchError(() => of(null))),
      testCases: this.testCaseService.list(this.projectId).pipe(catchError(() => of([] as TestCase[]))),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ plan, testCases }) => {
          if (!plan) {
            this.loadError.set(true);
            this.loading.set(false);
            return;
          }
          this.plan.set(plan);
          const byId = new Map(testCases.map((tc) => [tc.id, tc]));
          const assigned = plan.testCaseIds.map((id) => byId.get(id)).filter((tc): tc is TestCase => !!tc);
          this.rows.set(assigned.map((tc) => this.toRow(plan, tc)));
          this.loading.set(false);
        },
        error: () => {
          this.loadError.set(true);
          this.loading.set(false);
        },
      });
  }

  private loadDetails(): void {
    if (!this.planId) return;
    this.planService
      .getReportDetails(this.projectId, this.planId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (entries) => this.details.set(new Map(entries.map((entry) => [entry.testCaseId, entry]))),
        error: () => {
          this.detailsFailed.set(true);
          this.basis.set('status');
        },
      });
  }

  private toRow(plan: TestPlan, tc: TestCase): ReportRow {
    return {
      planPublicId: plan.publicId,
      planId: plan.id,
      testCaseKey: tc.key ?? tc.id.substring(0, 8),
      testCaseId: tc.id,
      summary: tc.title,
      status: this.mapStatus(plan.lastExecutionStatusByCase?.[tc.id] ?? null),
      requirementId: tc.requirementId ?? null,
      requirementLabel: tc.requirementLabel ?? null,
    };
  }

  private mapStatus(status: TestExecutionStatus | null): ResultBadgeKind {
    switch (status) {
      case 'PASSED':
        return 'passed';
      case 'FAILED':
        return 'failed';
      case 'RUNNING':
        return 'in-progress';
      default:
        return 'open';
    }
  }

  onSearchInput(event: Event): void {
    this.search.set((event.target as HTMLInputElement).value);
    this.writeQueryParams({ suche: this.searchParam() });
  }

  onBasisChange(basis: ReportBasis): void {
    this.basis.set(basis);
    this.writeQueryParams({ basis });
  }

  async onCopyFilter(): Promise<void> {
    const copied = await copyText(this.filterUrl());
    this.showMessage(copied ? 'testPlans.report.copyFilterDone' : 'testPlans.report.copyFilterFailed');
  }

  onExportPdf(): void {
    const plan = this.plan();
    if (!plan?.releaseId || this.pdfBusy()) return;
    this.pdfBusy.set(true);
    this.releaseReportService
      .generate(
        this.projectId,
        plan.releaseId,
        { art: 'ABSCHLUSS', umfang: 'PLAN', planId: plan.id, format: 'PDF' },
        plan.publicId ?? plan.id,
      )
      .pipe(
        finalize(() => this.pdfBusy.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: ({ blob, filename }) => saveBlob(blob, filename),
        error: (err: unknown) => this.showMessage(writeErrorKey(err, 'testPlans.report.exportPdfError')),
      });
  }

  private searchParam(): string | null {
    return this.search().trim() ? this.search() : null;
  }

  private writeQueryParams(queryParams: { suche?: string | null; basis?: ReportBasis }): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private filterUrl(): string {
    const tree = this.router.createUrlTree(['/projects', this.projectId, 'test-plans', this.planId, 'report'], {
      queryParams: { suche: this.searchParam(), basis: this.basis() },
    });
    return window.location.origin + this.location.prepareExternalUrl(this.router.serializeUrl(tree));
  }

  private showMessage(key: string): void {
    this.snackBar.open(this.translate.instant(key), undefined, { duration: MESSAGE_DURATION_MS });
  }

  onStart(): void {
    this.router.navigate(['/projects', this.projectId, 'test-runs', 'new'], {
      queryParams: realRunQueryParams(this.planId, this.plan()?.executionMode === 'AUTOMATED'),
    });
  }

  onBack(): void {
    this.router.navigate(['/projects', this.projectId, 'test-plans']);
  }

  onOpenResults(row: ReportRow): void {
    this.router.navigate(['/projects', this.projectId, 'test-results', 'history', row.testCaseId]);
  }

  onOpenPlan(): void {
    this.router.navigate(['/projects', this.projectId, 'test-plans', this.planId, 'edit']);
  }
}
